import type { ReactNode } from 'react';
import { MIN_QUERY } from '@/queries/people';

// The result list under a staff type-ahead. Rendered inline rather than in a
// popover: it lives inside a dialog or the narrow rail, where a floating menu
// would fight the dialog's focus trap and the rail's scroll.
export function SearchResults<T extends { _id: string }>({
  label,
  term,
  items,
  isFetching,
  onPick,
  renderItem
}: {
  label: string;
  term: string;
  items: T[] | undefined;
  isFetching: boolean;
  onPick: (item: T) => void;
  renderItem: (item: T) => ReactNode;
}) {
  if (term.trim().length < MIN_QUERY) return null;
  // Results kept from the previous query don't match what's typed now, so they aren't offered.
  if (isFetching || !items?.length) {
    return <p className="mt-1.5 text-etn-micro text-etn-text-soft">{isFetching ? 'Searching…' : 'No matches'}</p>;
  }
  // A plain list of buttons: Tab and Enter reach every result without a full combobox pattern.
  return (
    <ul aria-label={label} className="mt-1.5 max-h-56 overflow-y-auto rounded-etn-menu border border-etn-border-hairline bg-etn-surface py-1">
      {items.map(item => (
        <li key={item._id}>
          <button
            type="button"
            className="w-full px-3 py-1.5 text-left text-etn-control hover:bg-etn-control-subtle focus-visible:bg-etn-control-subtle focus-visible:outline-none"
            onClick={() => onPick(item)}
          >
            {renderItem(item)}
          </button>
        </li>
      ))}
    </ul>
  );
}
